import { clone } from "ramda";
import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { ActionButtonIntent, formatDateTimeFromISO, LoadingSpinner, TriggerButton, TriggerButtonIntent } from "widgets";

import type { ISelectionModel } from "@/app-utils/selection-model.ts";
import type { User } from "@/graphql.ts";
import type { IIdentifiable, ITableColumn } from "@/redux/modules/table-data.ts";
import {
  AUTHENTICATION_MODE,
  fetchNumberOfFreeSeats,
  invalidatePasswords,
  loadAllSessions,
  noMoreFreeSeatsText,
} from "@/redux/modules/users-admin.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { CELL_TYPE, SORT_ORDER_ASC } from "@/settings/settings.ts";
import DataContextIndicator from "@/ui-modules/core/grid/DataContextIndicator.tsx";

import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import { SelectItem, SelectWidget } from "../core/forms/SelectWidget.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import { ConfirmationDialog } from "../core/overlays/Dialogs.js";
import { ModalActionWidget, ModalControlledOverlay } from "../core/overlays/index.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import AddNewUserDialog from "./AddNewUserDialog.js";
import EditUserDialog from "./EditUserDialog.js";

interface IUserAdminPageAppStateProps {
  sessions: User[];
  isLoadingSessions: boolean;
  numberOfFreeSeats: number;
  authenticationMode: string;
}

interface IUserAdminPageDispatchProps {
  loadUsers: () => Promise<User[]>;
  loadNumberOfFreeSeats: () => Promise<number>;
  invalidatePasswords: (userIds: number[]) => Promise<boolean>;
}

export interface IUserAdminPageProps extends IUserAdminPageAppStateProps, IUserAdminPageDispatchProps {}

function mapStateToProps(state: IAppState): IUserAdminPageAppStateProps {
  return {
    sessions: state.usersAdmin.users,
    isLoadingSessions: state.usersAdmin.isLoadingUsers,
    numberOfFreeSeats: state.usersAdmin.numberOfFreeSeats,
    authenticationMode: state.config.configuration?.authenticationMode,
  };
}

function mapDispatchToProps(dispatch): IUserAdminPageDispatchProps {
  return {
    loadUsers: () => dispatch(loadAllSessions()),
    loadNumberOfFreeSeats: () => dispatch(fetchNumberOfFreeSeats()),
    invalidatePasswords: (userIds: number[]) => dispatch(invalidatePasswords(userIds)),
  };
}

interface IUserAdminPageState {
  tableSelectionModel: ISelectionModel;
  searchTerms: string[];
  rowData: IIdentifiable[];
  columns: Partial<ITableColumn>[];

  showAddNewUserDialog: boolean;
  showEditUserDialog: boolean;

  showDisabledUsers: boolean;
}

/**
 * User Admin Page main component
 */
class UserAdminPage extends Component<IUserAdminPageProps, IUserAdminPageState> {
  getColumnDefinition = (): Partial<ITableColumn>[] => {
    return [
      {
        name: "Username",
        field: "user.username",
        cellType: CELL_TYPE.STRING,
        sort: { direction: SORT_ORDER_ASC.toLowerCase(), priority: null },
      },
      {
        name: "First Name",
        field: "user.firstName",
        cellType: CELL_TYPE.STRING,
      },
      {
        name: "Last Name",
        field: "user.lastName",
        cellType: CELL_TYPE.STRING,
      },
      {
        name: "Email",
        field: "user.email",
        cellType: CELL_TYPE.STRING,
      },
      {
        name: "Last Use",
        field: "user.lastUse",
        cellType: CELL_TYPE.DATE_TIME,
      },
      {
        name: "Account Status",
        field: "user.isActive",
        cellType: CELL_TYPE.BOOLEAN,
      },
      {
        name: "Password Status",
        field: "user.accountStatus",
        cellType: CELL_TYPE.STRING,
        hidden: this.props.authenticationMode !== AUTHENTICATION_MODE.ORACLE,
      },
      {
        name: "Roles",
        field: "user.roles.name",
        cellType: CELL_TYPE.STRING_LIST,
      },
    ];
  };

  private userToEdit: User = null;

  constructor(props: IUserAdminPageProps) {
    super(props);
    this.state = {
      tableSelectionModel: null,
      searchTerms: [],
      rowData: null,
      columns: [],
      showAddNewUserDialog: false,
      showEditUserDialog: false,
      showDisabledUsers: false,
    };
  }

  handleUsernameClicked = (user: User) => {
    this.userToEdit = user;
    this.setState({ showEditUserDialog: true });
  };

  private cellRendererParams = {
    "user.username": {
      handleUsernameClicked: this.handleUsernameClicked,
    },
  };

  loadUsers = () => {
    this.props.loadNumberOfFreeSeats();
    // without resetting the rowData before populating again, ag-grid passes the previous version of the user to the cell renderer,
    // although it shows the correct data in the grid
    this.setState({ rowData: [] });
    this.props.loadUsers().then((sessions: User[]) => {
      this.setState({ rowData: this.createRowData(this.state.searchTerms, this.state.showDisabledUsers, sessions) });
    });
  };

  componentDidMount() {
    this.setState({ columns: this.getColumnDefinition() });
    this.loadUsers();
  }

  objectToFlatStringValueArray = (object: any): string[] => {
    return JSON.stringify(object, null, " ")
      .split("\n")
      .filter((r) => !r.match(/"__typename"|[{}\[\]]/))
      .map((l) => {
        const v = l.substr(l.indexOf(":") + 2);
        if (v.startsWith('"')) {
          return v.substr(1, v.length - 3);
        } else {
          return v.substr(0, v.length - 1);
        }
      })
      .filter((k) => k !== "null");
  };

  userPropContainsTerm = (user: User, terms: string[]) => {
    if (terms === null || terms.length === 0) {
      return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, prefer-const
    let { userGroups, capabilities, id, ...filterableFields } = user; // do not filter by user group and some others [SEL6-2692]

    filterableFields = {
      ...filterableFields,
      roles: filterableFields.roles.map((r) => {
        return { ...r, id: null };
      }), // get rid of role IDs
      lastUse: formatDateTimeFromISO(filterableFields.lastUse), // apply correct data/time formatting
    };

    const fieldValues = this.objectToFlatStringValueArray(filterableFields).map((k) =>
      k === "true" ? "Active" : k === "false" ? "Disabled" : k,
    );
    for (let i = 0; i < terms.length; ++i) {
      let foundTerm = false;
      for (let j = 0; j < fieldValues.length; ++j) {
        if (fieldValues[j].toLowerCase().includes(terms[i])) {
          foundTerm = true;
          break;
        }
      }
      if (!foundTerm) {
        return false;
      }
    }
    return true;
  };

  createRowData = (terms: string[], showDisabledUsers: boolean, users: User[]): IIdentifiable[] => {
    return users
      .filter((user: User) => this.userPropContainsTerm(user, terms))
      .filter((user: User) => (showDisabledUsers ? true : user.isActive))
      .map((user: User) => {
        return {
          id: user.id,
          user: clone(user), // we have to clone the user because of a bug in ag-grid 13 (AG-771)
        };
      });
  };

  selectionModelUpdated = (selectionModel: ISelectionModel) => {
    this.setState({ tableSelectionModel: selectionModel });
  };

  searchExprChanged = (_expr: string, terms: string[]) => {
    return new Promise<void>((resolve) => {
      const newRowData = this.createRowData(terms, this.state.showDisabledUsers, this.props.sessions);
      this.setState({ rowData: newRowData, searchTerms: terms });
      resolve();
    });
  };

  disabledUserVisibilityChanged = (showDisabledUsers: boolean) => {
    return new Promise<void>((resolve) => {
      const newRowData = this.createRowData(this.state.searchTerms, showDisabledUsers, this.props.sessions);
      this.setState({ rowData: newRowData, showDisabledUsers: showDisabledUsers });
      resolve();
    });
  };

  getDisabledUserVisibilityWidget = (props: { value: boolean; onValueChanged: (value: boolean) => void }) => {
    const trueString = "true";
    const falseString = "false";
    const valueString = props.value ? trueString : falseString;

    return (
      <SelectWidget
        icon={"person_off"}
        label={"Disabled users"}
        selectionMode={"single"}
        key={"disabled-users-widget"}
        selectedKeys={[valueString]}
        onSelectionChange={(selection) => {
          props.onValueChanged(String([...selection][0]) === trueString);
        }}
      >
        <SelectItem key={trueString}>Show</SelectItem>
        <SelectItem key={falseString}>Hide</SelectItem>
      </SelectWidget>
    );
  };

  render(): ReactElement {
    return (
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        <PageHeader
          title={"Users"}
          toolsWidgets={[
            this.getDisabledUserVisibilityWidget({
              value: this.state.showDisabledUsers,
              onValueChanged: this.disabledUserVisibilityChanged,
            }),
            <AsyncSearchWithExprParser onSearchExprChange={this.searchExprChanged} key={"user-admin-page-widget-1"} />,
          ]}
          actionWidgets={[
            <ModalActionWidget
              key={"user-admin-page-action-1"}
              actionWidget={({ open }) => (
                <TriggerButton
                  tooltip={`Add a new user account`}
                  onPress={() => open()}
                  isDisabled={this.props.numberOfFreeSeats === 0 ? noMoreFreeSeatsText() : undefined}
                  icon={"person_add"}
                  label={"New User"}
                  showLabel
                />
              )}
            >
              {({ close }) => <AddNewUserDialog onClose={close} refreshListOfUsers={this.loadUsers} />}
            </ModalActionWidget>,
            <ModalActionWidget
              key={"user-admin-invalidate-password-action"}
              actionWidget={({ open }) => (
                <TriggerButton
                  id={"user-admin-invalidate-password"}
                  tooltip={`Expire the password of the selected users`}
                  onPress={() => open()}
                  isDisabled={
                    this.props.authenticationMode !== AUTHENTICATION_MODE.ORACLE
                      ? "A directory service is used for password authentication"
                      : !this.state.tableSelectionModel?.selectedIds.length
                        ? "At least one row must be selected to expire passwords"
                        : undefined
                  }
                  intent={TriggerButtonIntent.Danger}
                  icon={"key_off"}
                  label={"Expire Password"}
                  showLabel
                />
              )}
            >
              {({ close }) => (
                <ConfirmationDialog
                  headerProps={{
                    title: "Expire Password",
                  }}
                  mainActionButtonProps={{
                    label: `Expire`,
                    onPress: () => this.invalidatePassword().then(() => close()),
                    intent: ActionButtonIntent.Danger,
                  }}
                  closeFn={close}
                >
                  {this.invalidatePasswordConfirmationMessage()}
                </ConfirmationDialog>
              )}
            </ModalActionWidget>,
          ]}
        />
        {this.props.isLoadingSessions ? (
          <LoadingSpinner inCenter />
        ) : (
          <div style={{ height: "calc(100% - 50px)" }}>
            <InMemoryTableWithSelectionModel
              columns={this.state.columns}
              rowGridData={this.state.rowData}
              useSelectionModel={true}
              selectionModelUpdated={this.selectionModelUpdated}
              uniqueGridIdentifier={"users-admin"}
              cellRendererParamsLookup={this.cellRendererParams}
            />
            <DataContextIndicator
              key="context-indicator"
              numRowsSelected={this.state.tableSelectionModel?.selectedIds.length}
              numTotalRows={this.props.sessions.length}
              numRowsFiltered={this.state.rowData?.length}
              rowEntityLabel={"user"}
              filterIsSet={!!this.state.searchTerms?.length}
            />
          </div>
        )}

        {this.state.showEditUserDialog && (
          <ModalControlledOverlay isOpen={true} close={() => this.setState({ showEditUserDialog: false })}>
            {({ close }) => (
              <EditUserDialog user={this.userToEdit} onClose={close} refreshListOfUsers={this.loadUsers} />
            )}
          </ModalControlledOverlay>
        )}
      </div>
    );
  }

  invalidatePasswordConfirmationMessage = () => {
    let usersMessage = `the ${this.state.tableSelectionModel.selectedIds.length} selected users`;
    if (this.state.tableSelectionModel.selectedIds.length === 1) {
      const selectedUserId = this.state.tableSelectionModel.selectedIds[0];
      const user: User = this.props.sessions.find((user: User) => user.id === selectedUserId);
      if (user) {
        usersMessage = `user '${user.username}'`;
      }
    }

    return `Are you sure you want to expire the password of ${usersMessage}?`;
  };

  invalidatePassword = () => {
    return this.props.invalidatePasswords(this.state.tableSelectionModel.selectedIds as number[]).then(() => {
      this.loadUsers();
    });
  };
}

export default connect<IUserAdminPageAppStateProps, IUserAdminPageDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(UserAdminPage);
